import { cn, Skeleton, Spinner, Typography } from "@norican/react";
import type { ComponentPropsWithoutRef } from "react";

import { useT } from "@/i18n";

export interface LoadingStateProps extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  /** Visible text under the spinner. Defaults to the translated "Loading…". */
  label?: string;
  /** Supporting line under the label. Pass `null` to suppress the default. */
  description?: string | null;
  /** Spinner size. Defaults to `md`. */
  size?: "sm" | "md" | "lg";
  /**
   * Render on one line instead of a centred block — for a loading indicator
   * beside a control rather than in place of a page.
   */
  inline?: boolean;
}

/**
 * The standard "we're fetching this" state.
 *
 * The design system `Spinner` already announces via `role="status"`, so the
 * visible label is translated and the spinner's own accessible name is set from
 * the same string rather than the English default baked into the component.
 */
export function LoadingState({
  label,
  description,
  size = "md",
  inline = false,
  className,
  ...props
}: LoadingStateProps) {
  const { t } = useT();
  const text = label ?? t("state.loading");

  if (inline) {
    return (
      <div className={cn("flex items-center gap-2", className)} {...props}>
        <Spinner size={size} tone="neutral" label={text} />
        <Typography variant="body-sm" tone="muted">
          {text}
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={cn("flex flex-col items-center justify-center gap-3 px-4 py-12", className)}
      {...props}
    >
      <Spinner size={size} tone="accent" label={text} />
      <Typography variant="body" tone="muted">
        {text}
      </Typography>
      {description === null ? null : (
        <Typography variant="body-sm" tone="muted">
          {description ?? t("state.loadingHint")}
        </Typography>
      )}
    </div>
  );
}

export interface LoadingRowsProps extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  /** How many placeholder rows to draw. Defaults to 5. */
  rows?: number;
}

/**
 * Skeleton rows for a list or table that is loading.
 *
 * `DataGrid` has no `loading` slot, so a caller renders this in place of the
 * grid while the query is in flight. Marked `aria-hidden` — the surrounding
 * live region or `LoadingState` does the announcing; a wall of skeletons read
 * aloud is noise.
 */
export function LoadingRows({ rows = 5, className, ...props }: LoadingRowsProps) {
  const { t } = useT();
  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      role="status"
      aria-label={t("a11y.loading")}
      {...props}
    >
      {Array.from({ length: rows }, (_unused, index) => (
        <Skeleton key={index} variant="rect" height="var(--norican-size-control-lg)" />
      ))}
    </div>
  );
}
